import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { take, tap, finalize } from 'rxjs/operators';
import { BehaviorSubject, Observable, catchError, throwError } from 'rxjs';

import { environment } from '@environment';
import { Client } from '@shared/types/client';
import { SessionService } from './session.service';

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  private http = inject(HttpClient);
  private sessionService = inject(SessionService);

  private _clients = new BehaviorSubject<Client[]>([]);
  private _isLoading = new BehaviorSubject<boolean>(false);

  readonly clients$ = this._clients.asObservable();
  readonly isLoading$ = this._isLoading.asObservable();

  constructor() {}

  fetchClients(): Observable<Client[]> {
    return this.http
      .get<Client[]>(environment.backendUrl + '/api/admin/client')
      .pipe(
        tap((clients) => this._clients.next(clients)),
        tap((clients) => this.sessionService.checkCurrentClient(clients)),
      );
  }

  fetchJwtSecret(): Observable<{ secret: string }> {
    return this.http.get<{ secret: string }>(
      environment.backendUrl + '/api/admin/client/jwt-secret',
    );
  }

  fetchApiSecret(): Observable<{ secret: string }> {
    return this.http.get<{ secret: string }>(
      environment.backendUrl + '/api/admin/client/api-secret',
    );
  }

  addClient(name: string): Observable<Client> {
    this._isLoading.next(true);
    return this.http
      .post<Client>(environment.backendUrl + '/api/admin/client', { name })
      .pipe(
        tap((client) => this._clients.next([...this._clients.value, client])),
        finalize(() => this._isLoading.next(false)),
      );
  }

  removeClient(client: Client): void {
    const clients = this._clients.value.filter((c) => c.uuid !== client.uuid);
    this._clients.next(clients);
    this.sessionService.changeClient(clients[0]);
  }

  updateClient(client: Client): Observable<Client> {
    this._isLoading.next(true);
    return this.http
      .put<Client>(
        environment.backendUrl + '/api/admin/client/' + client.uuid,
        client,
      )
      .pipe(
        tap((updatedClient) => {
          const clients = this._clients.value;
          const index = clients.findIndex((c) => c.uuid === updatedClient.uuid);
          clients[index] = updatedClient;
          this._clients.next(clients);
          this._isLoading.next(false);

          this.sessionService.client$
            .pipe(take(1))
            .subscribe((currentClient) => {
              if (currentClient?.uuid === updatedClient.uuid) {
                this.sessionService.changeClient(updatedClient);
              }
            });
        }),
        catchError((err: Error) => {
          this._isLoading.next(false);
          return throwError(() => new Error(err.message));
        }),
      );
  }
}
